import { TabsStateService } from './../../../../../shared/services/tabs-state.service';
import { LocalDatePipe } from '@anihis/shared/src/pipes/local-date.pipe';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  Input,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { UploadDocumentDialogComponent } from '../../../../../shared/component/upload-document-dialog/upload-document-dialog.component';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { UploadedFileDto } from '@anihis/core/data-access';
import { DocumentStore } from './documents.store';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { LimitToPipe } from '@anihis/shared/src/pipes/limit-character.pipe';
import { LoadingStatus } from '../../../../../shared/enums/loading';
import { TranslocoModule } from '@jsverse/transloco';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { LoadingSpinnerComponent } from '../../../../../shared/component/loading-spinner/loading-spinner.component';
import { LoadingSkeletonComponent } from '../../../../../shared/component/loading-skeleton/loading-spinner.component';
import { LoadingOverlayComponent } from '../../../../../shared/component/loading-overlay/loading-overlay.component';
import { EmptyFallbackHtmlPipe } from '../../../../../shared/pipe/empty-fallback-html.pipe';
import { MatChipsModule } from '@angular/material/chips';
import { AdminItemMobileCardComponent } from '../../../../../shared/component/admin-item-mobile-card/admin-item-mobile-card.component';

@Component({
  selector: 'anihis-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatIconModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatTooltipModule,
    MatNativeDateModule,
    InfiniteScrollDirective,
    LoadingSpinnerComponent,
    LoadingSkeletonComponent,
    LoadingOverlayComponent,
    EmptyFallbackHtmlPipe,
    LimitToPipe,
    MatChipsModule,
    AdminItemMobileCardComponent,
    LocalDatePipe,
  ],
  providers: [DocumentStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentsComponent extends FormBaseComponent {
  @Input() cardData!: UploadedFileDto[] | undefined;
  @Input() exitusLetalis!: boolean | undefined;

  private dialog = inject(MatDialog);
  store = inject(DocumentStore);
  private tabsStateService = inject(TabsStateService);

  @ViewChild('tableWrapper', { static: false })
  tableWrapperElement!: ElementRef;

  animalUid = input.required();
  refreshDocuments = input<number>();

  readonly status = LoadingStatus;

  isPreviewing = signal(false);
  onScrollDown = signal(false);

  override form = this.fb.group({
    range: this.fb.group({
      start: [null],
      end: [null],
    }),
    search: this.fb.control<string>(''),
  });

  displayedColumns: string[] = [
    'Name of Document',
    'Created By',
    'Name Clinic',
    'Created Date',
    'Actions',
  ];

  constructor() {
    super();
    explicitEffect([this.animalUid], ([animalUid]) => {
      if (!animalUid) return;
      this.store.setAnimalUid(animalUid as string);
      this.store.fetchDocuments().subscribe();
    });

    explicitEffect([this.refreshDocuments], ([refreshDocuments]) => {
      if (!refreshDocuments) return;
      this.store.fetchDocuments().subscribe();
    });

    this.form
      .get('search')
      ?.valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.store.updateSearch(value!);
      });

    effect(() => {
      const index = this.tabsStateService.selectedIndex();
      const currentTab = this.tabsStateService.tabs()[index];
      if (currentTab === 'Cards' && this.tableWrapperElement) {
        this.tableWrapperElement.nativeElement.scrollTop = 0;
      }
    });

    // React na kompletiran date range
    this.range.valueChanges.subscribe(({ start, end }) => {
      if (start && end) {
        this.store.updateDateRangeFilter(start, end);
      }
    });
  }

  get range(): FormGroup {
    return this.form.get('range') as FormGroup;
  }

  clearRange() {
    this.range.reset();
    this.store.updateDateRangeFilter(undefined, undefined);
  }

  clearSearch() {
    this.form.get('search')?.setValue('');
  }

  loadMore() {
    if (
      this.store.status() !== this.status.Loading &&
      this.store.pagination()?.hasNextPage
    ) {
      this.store.loadMoreData().subscribe();
    }
  }

  addDocument() {
    const animalUid = this.animalUid();

    const dialogRef = this.dialog.open(UploadDocumentDialogComponent, {
      width: '600px',
      autoFocus: false,
      disableClose: true,
      data: { animalUid },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.store.fetchDocuments().subscribe();
      }
    });
  }

  previewDocument(document: any) {
    if (!document || !document.uid) {
      return;
    }
    if (this.isPreviewing()) {
      return;
    }

    this.isPreviewing.set(true);
    this.store
      .getDocument(document.uid)
      .pipe(finalize(() => this.isPreviewing.set(false)))
      .subscribe({
        next: (url) => {
          if (url) window.open(url, '_blank');
        },
      });
  }
}
